import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  ChevronDown, ChevronRight, Plus,
  Video, FlaskConical, Briefcase, BookOpen, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  trainerContentApi,
  type CurriculumModule,
  type CurriculumTopic,
  type CreateTopicRequest,
  type CreateModuleRequest,
} from '@/lib/api/trainer';
import { cn } from '@/lib/cn';
import { TopicContentEditor } from './TopicContentEditor';
import { ConfirmDialog } from './ConfirmDialog';
import { ModuleForm } from './ModuleForm';
import { TopicForm } from './TopicForm';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  batchId: string;
  initialTopicId?: string;
}

interface PendingDelete {
  type: 'module' | 'topic';
  moduleId: string;
  topicId?: string;
  label: string;
}

const topicTypeIcon = (type: string) => {
  if (type === 'lab') return FlaskConical;
  if (type === 'project') return Briefcase;
  return Video;
};

export function CurriculumTab({ batchId, initialTopicId }: Props) {
  const [modules, setModules] = useState<CurriculumModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [activeTopic, setActiveTopic] = useState<string | null>(null);
  const navigate = useNavigate();

  // Mirror the selected topic into the URL (?topicId=…) so a refresh or shared
  // link restores the same topic instead of dropping to the empty state.
  const syncTopicToUrl = (id: string | null) => {
    navigate({ to: '.', search: (prev) => ({ ...prev, topicId: id ?? undefined }), replace: true });
  };

  type SheetMode =
    | { kind: 'add-module' }
    | { kind: 'add-topic'; moduleId: string }
    | { kind: 'edit-topic'; moduleId: string; topic: CurriculumTopic }
    | null;

  const [sheet, setSheet] = useState<SheetMode>(null);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

  const [search, setSearch] = useState('');

  // Match on module title OR topic title. A module-title match keeps all its
  // topics; otherwise we keep only the topics whose titles match.
  const query = search.trim().toLowerCase();
  const filteredModules = query
    ? modules
        .map((m) =>
          m.title.toLowerCase().includes(query)
            ? m
            : { ...m, topics: m.topics.filter((t) => t.title.toLowerCase().includes(query)) }
        )
        .filter((m) => m.title.toLowerCase().includes(query) || m.topics.length > 0)
    : modules;

  useEffect(() => {
    const q = search.trim().toLowerCase();
    if (!q) return;
    setExpandedModules(
      new Set(
        modules
          .filter((m) => m.title.toLowerCase().includes(q) || m.topics.some((t) => t.title.toLowerCase().includes(q)))
          .map((m) => m.id)
      )
    );
  }, [search, modules]);

  // Resizable left panel
  const [panelWidth, setPanelWidth] = useState(320);
  const resizing = useRef(false);
  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizing.current = true;
    const startX = e.clientX;
    const startW = panelWidth;
    const onMove = (ev: MouseEvent) => {
      if (!resizing.current) return;
      setPanelWidth(Math.min(480, Math.max(180, startW + ev.clientX - startX)));
    };
    const onUp = () => {
      resizing.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }, [panelWidth]);

  // Drag-and-drop for modules
  const dragModIdx = useRef<number | null>(null);
  const [dragOverModIdx, setDragOverModIdx] = useState<number | null>(null);

  // Drag-and-drop for topics
  const dragTopicKey = useRef<string | null>(null); // "moduleId:topicIndex"
  const [dragOverTopicKey, setDragOverTopicKey] = useState<string | null>(null);

  // Track whether we've already applied the initialTopicId (fires exactly once per ID)
  const topicApplied = useRef(false);

  const load = async () => {
    try {
      const res = await trainerContentApi.getCurriculum(batchId);
      setModules(res.data);
      if (res.data.length > 0)
        setExpandedModules((prev) => (prev.size > 0 ? prev : new Set([res.data[0].id])));
    } catch {
      toast.error('Failed to load curriculum');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [batchId]);

  // Fire exactly once per initialTopicId value (on the first successful match after modules load)
  useEffect(() => {
    if (!initialTopicId || modules.length === 0 || topicApplied.current) return;
    const parentModule = modules.find((m) => m.topics.some((t) => t.id === initialTopicId));
    if (parentModule) {
      setExpandedModules((prev) => new Set([...prev, parentModule.id]));
      setActiveTopic(initialTopicId);
      topicApplied.current = true;
    }
  }, [initialTopicId, modules]);

  // Reset the applied flag when initialTopicId changes (so a new topic ID triggers a new selection)
  useEffect(() => {
    topicApplied.current = false;
  }, [initialTopicId]);

  const toggleModule = (id: string) =>
    setExpandedModules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });

  const toggleTopic = (id: string) => {
    const next = activeTopic === id ? null : id;
    setActiveTopic(next);
    syncTopicToUrl(next);
  };

  const handleCreateModule = async (req: CreateModuleRequest) => {
    setSaving(true);
    try {
      const res = await trainerContentApi.createModule(batchId, {
        ...req,
        sort_order: modules.length + 1,
      });
      const mod = res.data;
      setModules((prev) => [...prev, mod]);
      setExpandedModules((prev) => new Set([...prev, mod.id]));
      setSheet(null);
    } catch {
      toast.error('Failed to create module');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteModule = (moduleId: string) => {
    const mod = modules.find((m) => m.id === moduleId);
    setPendingDelete({ type: 'module', moduleId, label: mod?.title ?? 'this module' });
  };

  const confirmDeleteModule = async (moduleId: string) => {
    try {
      await trainerContentApi.deleteModule(batchId, moduleId);
      setModules((prev) => prev.filter((m) => m.id !== moduleId));
      setExpandedModules((prev) => { const n = new Set(prev); n.delete(moduleId); return n; });
    } catch {
      toast.error('Failed to delete module');
    }
  };

  const handleCreateTopic = async (moduleId: string, req: CreateTopicRequest) => {
    const mod = modules.find((m) => m.id === moduleId);
    setSaving(true);
    try {
      const res = await trainerContentApi.createTopic(batchId, moduleId, {
        ...req,
        sort_order: (mod?.topics.length ?? 0) + 1,
      });
      setModules((prev) =>
        prev.map((m) =>
          m.id === moduleId
            ? { ...m, topics: [...m.topics, { ...res.data, session_count: 0, resource_count: 0, assessment_count: 0 }] }
            : m
        )
      );
      setSheet(null);
    } catch {
      toast.error('Failed to create topic');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTopic = async (moduleId: string, topic: CurriculumTopic, req: CreateTopicRequest) => {
    setSaving(true);
    try {
      const res = await trainerContentApi.updateTopic(batchId, topic.id, {
        title: req.title,
        description: req.description,
        topic_type: req.topic_type,
        estimated_hours: req.estimated_hours,
        sort_order: topic.sort_order,
      });
      setModules((prev) =>
        prev.map((m) =>
          m.id === moduleId
            ? { ...m, topics: m.topics.map((t) => (t.id === topic.id ? { ...t, ...res.data } : t)) }
            : m
        )
      );
      setSheet(null);
    } catch {
      toast.error('Failed to update topic');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTopic = (moduleId: string, topicId: string) => {
    const topic = modules.find((m) => m.id === moduleId)?.topics.find((t) => t.id === topicId);
    setPendingDelete({ type: 'topic', moduleId, topicId, label: topic?.title ?? 'this topic' });
  };

  const confirmDeleteTopic = async (moduleId: string, topicId: string) => {
    try {
      await trainerContentApi.deleteTopic(batchId, topicId);
      setModules((prev) =>
        prev.map((m) =>
          m.id === moduleId ? { ...m, topics: m.topics.filter((t) => t.id !== topicId) } : m
        )
      );
      if (activeTopic === topicId) { setActiveTopic(null); syncTopicToUrl(null); }
    } catch {
      toast.error('Failed to delete topic');
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    if (pendingDelete.type === 'module') {
      await confirmDeleteModule(pendingDelete.moduleId);
    } else if (pendingDelete.topicId) {
      await confirmDeleteTopic(pendingDelete.moduleId, pendingDelete.topicId);
    }
    setPendingDelete(null);
  };

  const handleModuleDrop = (dropIdx: number) => {
    const fromIdx = dragModIdx.current;
    setDragOverModIdx(null);
    dragModIdx.current = null;
    if (fromIdx === null || fromIdx === dropIdx) return;
    const reordered = [...modules];
    const [moved] = reordered.splice(fromIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    const updated = reordered.map((m, i) => ({ ...m, sort_order: i + 1 }));
    setModules(updated);
    updated.forEach((m, i) => {
      if (modules[i]?.id !== m.id) {
        trainerContentApi.updateModule(batchId, m.id, { sort_order: m.sort_order }).catch(() => {});
      }
    });
  };

  const handleTopicDrop = (moduleId: string, dropIdx: number) => {
    const key = dragTopicKey.current;
    setDragOverTopicKey(null);
    dragTopicKey.current = null;
    if (!key) return;
    const [fromModId, fromIdxStr] = key.split(':');
    const fromIdx = parseInt(fromIdxStr, 10);
    if (fromModId !== moduleId || fromIdx === dropIdx) return;
    setModules((prev) =>
      prev.map((m) => {
        if (m.id !== moduleId) return m;
        const reordered = [...m.topics];
        const [moved] = reordered.splice(fromIdx, 1);
        reordered.splice(dropIdx, 0, moved);
        const updated = reordered.map((t, i) => ({ ...t, sort_order: i + 1 }));
        updated.forEach((t, i) => {
          if (m.topics[i]?.id !== t.id) {
            trainerContentApi.updateTopic(batchId, t.id, { sort_order: t.sort_order }).catch(() => {});
          }
        });
        return { ...m, topics: updated };
      })
    );
  };

  return (
    <div className="flex overflow-hidden" style={{ height: 'calc(100vh - 124px)' }}>
      {/* Left outline panel */}
      <div
        className="flex flex-shrink-0 flex-col overflow-hidden border-r border-[#ececec] bg-white"
        style={{ width: panelWidth }}
      >
        <div className="flex-1 overflow-y-auto">
          {/* Search */}
          <div className="px-3 py-2 border-b border-[#ececec] sticky top-0 bg-white z-10">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#6b7280] pointer-events-none" />
              <input
                type="text"
                placeholder="Search topics or modules…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-md border border-[#ececec] bg-[#f3f4f6]/40 py-1.5 pl-8 pr-3 text-xs placeholder:text-[#6b7280] focus:outline-none focus:ring-1 focus:ring-[#ff5a1f]"
              />
            </div>
          </div>
          <div className="py-2">
          {loading ? (
            <p className="p-4 text-xs text-[#6b7280]">Loading…</p>
          ) : (
            <>
            {filteredModules.map((mod) => {
              const expanded = expandedModules.has(mod.id);
              const modIdx = modules.indexOf(mod);
              return (
                <div
                  key={mod.id}
                  draggable
                  onDragStart={() => { dragModIdx.current = modIdx; }}
                  onDragOver={(e) => { e.preventDefault(); setDragOverModIdx(modIdx); }}
                  onDragLeave={() => setDragOverModIdx(null)}
                  onDrop={() => handleModuleDrop(modIdx)}
                  className={cn(dragOverModIdx === modIdx && dragModIdx.current !== modIdx ? 'bg-[#fff1ec] outline outline-1 outline-primary/50' : '')}
                >
                  {/* Module header */}
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleModule(mod.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        toggleModule(mod.id);
                      }
                    }}
                    aria-expanded={expanded}
                    className="flex items-center gap-1.5 px-3 py-2 cursor-pointer hover:bg-[#fafafa]/40 transition-colors"
                  >
                    {expanded
                      ? <ChevronDown className="h-3.5 w-3.5 text-[#6b7280] flex-shrink-0" />
                      : <ChevronRight className="h-3.5 w-3.5 text-[#6b7280] flex-shrink-0" />}
                    <span className="text-xs font-semibold text-[#111827] flex-1 truncate">
                      {mod.title}
                    </span>
                    <span className="text-[10px] text-[#6b7280] flex-shrink-0">{mod.topics.length}</span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSheet({ kind: 'add-topic', moduleId: mod.id }); }}
                      aria-label={`Add topic to ${mod.title}`}
                      title="Add topic"
                      className="h-5 w-5 flex items-center justify-center rounded text-[#6b7280] hover:text-[#ff5a1f] hover:bg-[#fff1ec] transition-colors flex-shrink-0"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>

                  {expanded && (
                    <div>
                      {mod.topics.map((topic) => {
                        const Icon = topicTypeIcon(topic.topic_type);
                        const isActive = activeTopic === topic.id;
                        const topicIdx = mod.topics.indexOf(topic);
                        const topicKey = `${mod.id}:${topicIdx}`;
                        return (
                          <div
                            key={topic.id}
                            draggable={activeTopic !== topic.id}
                            onDragStart={() => { dragTopicKey.current = topicKey; }}
                            onDragOver={(e) => { e.preventDefault(); setDragOverTopicKey(topicKey); }}
                            onDragLeave={() => setDragOverTopicKey(null)}
                            onDrop={() => handleTopicDrop(mod.id, topicIdx)}
                            className={cn(dragOverTopicKey === topicKey ? 'bg-[#fff1ec]' : '')}
                          >
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => toggleTopic(topic.id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleTopic(topic.id);
                                }
                              }}
                              aria-pressed={isActive}
                              className={`flex items-start gap-2 pl-6 pr-3 py-2 cursor-pointer transition-colors ${
                                isActive
                                  ? 'bg-orange-50 border-l-2 border-orange-400'
                                  : 'hover:bg-[#fafafa]/30 border-l-2 border-transparent'
                              }`}
                            >
                              <Icon className="h-3.5 w-3.5 flex-shrink-0 text-[#6b7280] mt-0.5" />
                              <span className={`text-sm leading-snug flex-1 min-w-0 break-words ${isActive ? 'font-medium' : ''}`}>
                                {topic.title}
                              </span>
                              {(topic.session_count > 0 || topic.resource_count > 0 || topic.assessment_count > 0) && (
                                <span className="h-1.5 w-1.5 rounded-full bg-[#ff5a1f]/50 flex-shrink-0 self-center" />
                              )}
                            </div>
                          </div>
                        );
                      })}

                    </div>
                  )}
                </div>
              );
            })}
            {filteredModules.length === 0 && search.trim() && (
              <div className="px-4 py-6 text-center text-xs text-[#6b7280]">
                No topics match "{search}"
              </div>
            )}
            </>
          )}
          </div>
        </div>

        {/* Add module pinned to bottom */}
        <div className="border-t border-[#ececec] p-3">
          <button
            onClick={() => setSheet({ kind: 'add-module' })}
            className="flex items-center justify-center gap-1.5 w-full rounded-md border border-dashed border-[#ececec] py-2 text-xs text-[#6b7280] hover:border-[#ececec]/80 hover:text-[#111827] hover:bg-[#fafafa]/30 transition-colors"
          >
            <Plus className="h-3 w-3" /> Add Module
          </button>
        </div>
      </div>

      {/* Resize handle */}
      <div
        onMouseDown={startResize}
        className="w-1 flex-shrink-0 cursor-col-resize bg-transparent hover:bg-[#ff5a1f]/30 active:bg-[#ff5a1f]/50 transition-colors"
      />

      {/* Add Module dialog */}
      <Dialog open={sheet?.kind === 'add-module'} onOpenChange={(open) => { if (!open) setSheet(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add module</DialogTitle></DialogHeader>
          <ModuleForm onSave={handleCreateModule} onCancel={() => setSheet(null)} />
        </DialogContent>
      </Dialog>

      {/* Add Topic dialog */}
      <Dialog open={sheet?.kind === 'add-topic'} onOpenChange={(open) => { if (!open) setSheet(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add topic</DialogTitle></DialogHeader>
          {sheet?.kind === 'add-topic' && (
            <TopicForm
              onSave={(req) => handleCreateTopic(sheet.moduleId, req)}
              onCancel={() => setSheet(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Topic dialog */}
      <Dialog open={sheet?.kind === 'edit-topic'} onOpenChange={(open) => { if (!open) setSheet(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit topic</DialogTitle></DialogHeader>
          {sheet?.kind === 'edit-topic' && (
            <TopicForm
              initial={{
                title: sheet.topic.title,
                topic_type: sheet.topic.topic_type,
                estimated_hours: sheet.topic.estimated_hours,
                description: sheet.topic.description,
              }}
              onSave={(req) => handleUpdateTopic(sheet.moduleId, sheet.topic, req)}
              onCancel={() => setSheet(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={pendingDelete !== null}
        title={pendingDelete?.type === 'module' ? 'Delete module?' : 'Delete topic?'}
        description={
          pendingDelete?.type === 'module'
            ? `"${pendingDelete.label}" and all its topics will be permanently deleted.`
            : `"${pendingDelete?.label}" and all its content will be permanently deleted.`
        }
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      {/* Right content panel */}
      <div className="flex-1 overflow-y-auto bg-white">
        {loading ? (
          <p className="p-6 text-sm text-[#6b7280]">Loading…</p>
        ) : modules.length === 0 ? (
          <div className="flex h-full min-h-[400px] flex-col items-center justify-center text-center px-6">
            <BookOpen className="h-10 w-10 text-[#9ca3af]" />
            <p className="mt-3 text-sm font-medium text-[#111827]">No curriculum yet</p>
            <p className="mt-1 text-xs text-[#6b7280]">
              Add your first module from the panel on the left to get started.
            </p>
          </div>
        ) : !activeTopic ? (
          <div className="flex h-full min-h-[400px] flex-col items-center justify-center text-center px-6">
            <p className="text-sm text-[#6b7280]">
              <span aria-hidden="true">←</span> Select a topic from the outline to manage its content
            </p>
          </div>
        ) : (
          (() => {
            const owner = modules.find((m) => m.topics.some((t) => t.id === activeTopic));
            const activeTopicData = owner?.topics.find((t) => t.id === activeTopic);
            return (
              <TopicContentEditor
                batchId={batchId}
                topicId={activeTopic}
                topic={activeTopicData}
                onTopicUpdated={(u) =>
                  setModules((prev) =>
                    prev.map((m) =>
                      m.id === owner?.id
                        ? { ...m, topics: m.topics.map((t) => (t.id === u.id ? { ...t, ...u } : t)) }
                        : m
                    )
                  )
                }
                onEditTopic={
                  owner && activeTopicData
                    ? () => setSheet({ kind: 'edit-topic', moduleId: owner.id, topic: activeTopicData })
                    : undefined
                }
                onDeleteTopic={
                  owner && activeTopicData
                    ? () => handleDeleteTopic(owner.id, activeTopicData.id)
                    : undefined
                }
              />
            );
          })()
        )}
      </div>
    </div>
  );
}
